import React, { useState } from 'react';
// Layout is provided by App.tsx route wrapper
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { User, Mail, Lock, Briefcase, Save, Camera, AlertCircle, CheckCircle } from 'lucide-react';

export const UserProfilePage = () => {
    const { user } = useAuth();
    const { theme } = useTheme();
    const { language } = useLanguage();

    const isDark = theme === 'dark';
    const bgCard = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]';
    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const inputBg = isDark ? 'bg-white border-[var(--border)]' : 'bg-gray-50 border-gray-300';

    const [formData, setFormData] = useState({
        full_name: user?.full_name || '',
        job_title: language === 'es' ? 'Analista de Operaciones' : 'Operations Analyst',
        password: '',
        confirmPassword: ''
    });
    const [status, setStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus(null);
        if (formData.password && formData.password !== formData.confirmPassword) {
            setStatus({ type: 'error', msg: language === 'es' ? 'Las contraseñas no coinciden.' : 'Passwords do not match.' });
            return;
        }
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setStatus({ type: 'success', msg: language === 'es' ? 'Perfil actualizado correctamente.' : 'Profile updated successfully.' });
        }, 1000);
    };

    return (
        <>
            <div className="w-full max-w-4xl mx-auto space-y-8">
                <h1 className={`text-3xl font-bold flex items-center gap-3 ${textMain}`}>
                    <User className="w-8 h-8 text-[#F26430]" />
                    {language === 'es' ? 'Mi Perfil Profesional' : 'My Professional Profile'}
                </h1>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className={`col-span-1 p-6 rounded-xl border shadow-sm flex flex-col items-center ${bgCard}`}>
                        <div className="relative group cursor-pointer mb-6">
                            <div className="w-40 h-40 rounded-full bg-gradient-to-tr from-blue-900 to-[#1C1C1C] p-1 border-4 border-[#F26430] shadow-2xl">
                                <div className="w-full h-full rounded-full flex items-center justify-center text-5xl font-bold text-white bg-[var(--surface-inset)] overflow-hidden">
                                    {user?.avatar_url ? <img src={user.avatar_url} className="w-full h-full object-cover" /> : user?.full_name?.charAt(0)}
                                </div>
                            </div>
                        </div>
                        <h2 className={`text-xl font-bold ${textMain}`}>{user?.full_name}</h2>
                        <p className={`text-sm ${textSub}`}>{user?.role || 'Analista'}</p>
                    </div>

                    <div className={`col-span-2 p-8 rounded-xl border shadow-sm ${bgCard}`}>
                        {status && (
                            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${status.type === 'success' ? 'bg-green-900/20 text-green-400 border border-green-500/30' : 'bg-red-900/20 text-red-400 border border-red-500/30'}`}>
                                {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                                {status.msg}
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 border-b pb-2 ${isDark ? 'border-[var(--border)] text-[var(--text-tertiary)]' : 'border-[var(--border)] text-[var(--text-secondary)]'}`}>
                                    {language === 'es' ? 'Información Personal' : 'Personal Information'}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`block text-xs font-bold mb-2 uppercase ${textSub}`}>
                                            {language === 'es' ? 'Nombre Completo' : 'Full Name'}
                                        </label>
                                        <input name="full_name" value={formData.full_name} onChange={handleChange} className={`w-full p-2.5 rounded-lg border outline-none text-sm focus:border-[#F26430] ${inputBg} ${textMain}`} />
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-bold mb-2 uppercase ${textSub}`}>
                                            {language === 'es' ? 'Puesto / Cargo' : 'Job Title'}
                                        </label>
                                        <input name="job_title" value={formData.job_title} onChange={handleChange} className={`w-full p-2.5 rounded-lg border outline-none text-sm focus:border-[#F26430] ${inputBg} ${textMain}`} />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={`block text-xs font-bold mb-2 uppercase ${textSub}`}>
                                            Email <span className="text-red-500 ml-1 text-[10px]">({language === 'es' ? 'Solo Admin' : 'Admin Only'})</span>
                                        </label>
                                        <div className="relative">
                                            <input value={user?.email} disabled className={`w-full pl-10 p-2.5 rounded-lg border border-transparent bg-gray-500/10 text-[var(--text-tertiary)] text-sm cursor-not-allowed`} />
                                            <Lock className="absolute left-3 top-3 w-4 h-4 text-[var(--text-tertiary)]" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-4 flex justify-end">
                                <button type="submit" disabled={loading} className="flex items-center gap-2 bg-[#F26430] hover:bg-[#d95325] text-white px-8 py-3 rounded-lg font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-50">
                                    {loading
                                        ? (language === 'es' ? 'Guardando...' : 'Saving...')
                                        : <><Save size={18} /> {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};